import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { apiClient } from "@/lib/api";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { Star, MapPin, Hammer, Award, BadgeCheck, ArrowUpRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Artisans() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [ratingMin, setRatingMin] = useState(0);
  const [badge, setBadge] = useState("");
  const [sort, setSort] = useState("");

  useEffect(() => {
    const run = async () => {
      const params = {
        q: q || undefined,
        city: city || undefined,
        rating_min: ratingMin > 0 ? ratingMin : undefined,
        badge: badge || undefined,
        sort: sort || undefined,
      };
      const { data } = await apiClient.get("/artisans", { params });
      setList(data.artisans || []);
      setTotal(data.total ?? (data.artisans || []).length);
    };
    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  }, [q, city, ratingMin, badge, sort]);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <SEO cfg={getSEO("/artisans")} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Annuaire national</div>
            <h1 className="display text-4xl lg:text-5xl mt-2 leading-tight">
              Annuaire artisans BTP SIRET vérifiés · <span className="text-[#FF4D00]">France entière</span>
            </h1>
            <p className="text-sm text-zinc-600 mt-3 max-w-2xl">
              Trouvez un artisan de confiance : plombier, électricien, maçon, menuisier, peintre, carreleur, chauffagiste, couvreur, plaquiste, isolation.
              Identité légale contrôlée en temps réel via la base Sirene/INSEE. Paiement sécurisé par séquestre Stripe.
            </p>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3" data-testid="artisans-total">
              {total} artisan{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, société, spécialité…"
              className="border-2 border-[#0A0A0B] px-4 py-3 min-w-[240px] bg-white"
              data-testid="artisans-search"
            />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ville"
              className="border-2 border-[#0A0A0B] px-4 py-3 bg-white"
              data-testid="artisans-city"
            />
          </div>
        </div>

        {/* v2 filters row */}
        <h2 className="sr-only">Filtrer les artisans par badge, note et spécialité</h2>
        <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b-2 border-[#0A0A0B]">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Filtres :</span>
          {[
            { v: "", l: "Tous badges" },
            { v: "gold", l: "🏆 Or" },
            { v: "premium", l: "⭐ Premium+" },
            { v: "verified", l: "✓ SIRET vérifié" },
          ].map((b) => (
            <button
              key={b.v || "all"}
              onClick={() => setBadge(b.v)}
              data-testid={`filter-badge-${b.v || "all"}`}
              className={`text-xs px-3 py-2 border-2 transition ${badge === b.v ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "bg-white border-zinc-300 hover:border-[#FF4D00]"}`}
            >
              {b.l}
            </button>
          ))}
          <span className="ml-4 mono text-[10px] uppercase tracking-widest text-zinc-500">Note min :</span>
          {[0, 3, 4, 4.5].map((r) => (
            <button
              key={r}
              onClick={() => setRatingMin(r)}
              data-testid={`filter-rating-${r}`}
              className={`text-xs px-3 py-2 border-2 transition ${ratingMin === r ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white border-zinc-300 hover:border-[#FF4D00]"}`}
            >
              {r === 0 ? "Toutes" : `${r}★+`}
            </button>
          ))}
          <span className="ml-4 mono text-[10px] uppercase tracking-widest text-zinc-500">Tri :</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="filter-sort"
            className="text-xs px-3 py-2 border-2 border-zinc-300 bg-white hover:border-[#FF4D00]"
          >
            <option value="">Recommandation</option>
            <option value="rating">Note ⭐</option>
            <option value="certificates">Certificats émis</option>
            <option value="recent">Récemment inscrits</option>
          </select>
        </div>

        {list.length === 0 && (
          <div className="border-2 border-[#0A0A0B] bg-white p-10 text-center">
            <Hammer className="w-8 h-8 mx-auto text-[#FF4D00]" />
            <div className="display text-2xl mt-3">Aucun artisan trouvé</div>
            <div className="text-sm text-zinc-500 mt-2">Affinez votre recherche ou revenez plus tard.</div>
          </div>
        )}

        <h2 className="sr-only">Liste des artisans du bâtiment vérifiés SIRET disponibles en France</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[#0A0A0B] bg-white">
          {list.map((a, i) => (
            <div
              key={a.id}
              className={`p-6 border-zinc-200 ${i % 3 !== 2 ? "border-r" : ""} ${i < list.length - (list.length % 3 || 3) ? "border-b" : ""} lift relative ${a.is_gold ? "bg-gradient-to-br from-amber-50 to-white" : a.is_premium ? "bg-gradient-to-br from-orange-50 to-white" : ""}`}
              data-testid={`artisan-card-${a.id}`}
            >
              {a.is_gold ? (
                <div className="absolute top-0 right-0 bg-amber-500 text-[#0A0A0B] px-2 py-1 mono text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" data-testid={`artisan-badge-gold-${a.id}`}>
                  ★ Or
                </div>
              ) : a.is_premium && (
                <div className="absolute top-0 right-0 bg-[#FF4D00] text-white px-2 py-1 mono text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" data-testid={`artisan-badge-premium-${a.id}`}>
                  ★ Premium
                </div>
              )}
              {a.siret_verified && (
                <div className="absolute top-0 left-0 bg-emerald-600 text-white px-2 py-1 mono text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" title={a.siret_denomination}>
                  ✓ SIRET
                </div>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <div className="display text-xl flex items-center gap-2">{a.name}</div>
                  {a.company && <div className="text-xs text-zinc-500">{a.company}</div>}
                </div>
                <div className="flex items-center gap-1 mono text-xs">
                  <Star className="w-3.5 h-3.5 text-[#FF4D00]" fill="currentColor" />
                  {a.rating_avg ? a.rating_avg.toFixed(1) : "—"}
                  <span className="text-zinc-400">({a.rating_count || 0})</span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-zinc-600 mt-2">
                <MapPin className="w-3.5 h-3.5" /> {a.city || "—"}
              </div>
              {a.has_decennale && (
                <div
                  className="inline-flex items-center gap-1 mt-2 mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-300 px-2 py-0.5"
                  data-testid={`artisan-badge-decennale-${a.id}`}
                  title={`Assurance décennale ${a.decennale_company}${a.decennale_valid_until ? ` · valide jusqu'au ${a.decennale_valid_until.split("-").reverse().join("/")}` : ""}`}
                >
                  <ShieldCheck className="w-3 h-3" /> Décennale
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-4">
                {(a.specialties || []).slice(0, 5).map((s) => (
                  <span key={s} className="mono text-[10px] uppercase tracking-widest border border-[#0A0A0B] px-2 py-0.5">
                    {s}
                  </span>
                ))}
                {(a.specialties || []).length > 5 && (
                  <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">+{a.specialties.length - 5}</span>
                )}
              </div>
              {a.bio && <p className="text-xs text-zinc-600 mt-4 line-clamp-3">{a.bio}</p>}
              <Link
                to={`/artisans/${a.id}`}
                className="mt-4 mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold inline-flex items-center gap-1 hover:underline"
                data-testid={`artisan-view-${a.id}`}
              >
                Voir le profil <ArrowUpRight className="w-3 h-3" />
              </Link>
              {(a.certificates_count > 0 || a.completed_missions > 0) && (
                <div className="mt-4 pt-3 border-t border-zinc-200 grid grid-cols-2 gap-3 text-[11px]" data-testid={`artisan-trust-${a.id}`}>
                  {a.certificates_count > 0 && (
                    <div className="flex items-center gap-1.5">
                      <Award className="w-3.5 h-3.5 text-amber-600" />
                      <span><strong>{a.certificates_count}</strong> certificat{a.certificates_count > 1 ? "s" : ""} Batisur</span>
                    </div>
                  )}
                  {a.completed_missions > 0 && (
                    <div className="flex items-center gap-1.5">
                      <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
                      <span><strong>{a.completed_missions}</strong> chantier{a.completed_missions > 1 ? "s" : ""} terminé{a.completed_missions > 1 ? "s" : ""}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
